import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FiX, FiCheck, FiFileText, FiTag, FiMapPin, 
  FiMap, FiNavigation, FiGlobe, FiPhone, FiMail, 
  FiHash, FiUser } from 'react-icons/fi';
import { FaBuilding } from "react-icons/fa";
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  BASE_URL,
  POST_SUPPLIER_MASTER,
  PUT_SUPPLIER_MASTER,
  POST_MANUFACTURER_MASTER,
  PUT_MANUFACTURER_MASTER
} from '../utlilities/config';

const AddSupplierModal = ({ setShowModal, onSave, selectedItem, setSelectedItem, mode }) => {
  const [loading, setLoading] = useState(false);
  const isSupplier = mode === 'Supplier';
  const token = localStorage.getItem('accessToken');

  // Create refs for keyboard navigation
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const shortNameRef = useRef(null);
  const addressRef = useRef(null);
  const placeRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const pincodeRef = useRef(null);
  const mobileRef = useRef(null);
  const phone1Ref = useRef(null);
  const phone2Ref = useRef(null);
  const emailRef = useRef(null);
  const gstNumRef = useRef(null);
  const contactPersonNameRef = useRef(null);
  const contactPersonNumberRef = useRef(null);
  const creditDaysRef = useRef(null);
  const supplierSourceRef = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    // Focus on first field after modal opens
    setTimeout(() => {
      codeRef.current && codeRef.current.focus();
    }, 100);
  }, []);

  useEffect(() => {
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  const handleCancel = () => {
    setSelectedItem(null);
    setShowModal();
  };

  // Handle keyboard navigation between fields
  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  // Validation schema based on mode
  const getValidationSchema = () => {
    if (isSupplier) {
      return Yup.object().shape({
        supplier_code: Yup.string().required('Supplier Code is required'),
        supplier_name: Yup.string().required('Supplier Name is required'),
        address: Yup.string(),
        place: Yup.string(),
        city: Yup.string(),
        state: Yup.string(),
        pincode: Yup.string().matches(/^[0-9]{6}$|^$/, 'Pincode must be 6 digits or empty'),
        mobile_no: Yup.string().matches(/^[0-9]{10}$|^$/, 'Mobile number must be 10 digits or empty'),
        phone1: Yup.string().matches(/^[0-9]{10}$|^$/, 'Phone must be 10 digits or empty'),
        phone2: Yup.string().matches(/^[0-9]{10}$|^$/, 'Phone must be 10 digits or empty'),
        email: Yup.string().email('Invalid email address'),
        gst_num: Yup.string(),
        contactperson_name: Yup.string(),
        contactperson_number: Yup.string().matches(/^[0-9]{10}$|^$/, 'Contact number must be 10 digits or empty'),
        credit_days: Yup.number()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .typeError('Credit days must be a number')
          .min(0, 'Credit days cannot be negative'),
        supplier_source: Yup.string().required('Supplier Category is required'),
      });
    } else {
      return Yup.object().shape({
        code: Yup.string().required('Code is required'),
        name: Yup.string().required('Name is required'),
        shortname: Yup.string(),
        address: Yup.string(),
        place: Yup.string(),
        contactperson: Yup.string(),
        number: Yup.string().matches(/^[0-9]{10}$|^$/, 'Number must be 10 digits or empty'),
      });
    }
  };

  // Initial values based on mode
  const getInitialValues = () => {
    if (isSupplier) {
      return {
        supplier_code: selectedItem?.SupplierCode || '',
        supplier_name: selectedItem?.SupplierName || '',
        address: selectedItem?.Address || '',
        place: selectedItem?.Place || '',
        city: selectedItem?.City || '',
        state: selectedItem?.State || '',
        pincode: selectedItem?.Pincode || '',
        mobile_no: selectedItem?.MobileNo || '',
        phone1: selectedItem?.Phone1 || '',
        phone2: selectedItem?.Phone2 || '',
        email: selectedItem?.Email || '',
        gst_num: selectedItem?.GstNo || '',
        contactperson_name: selectedItem?.ContactPersonName || '',
        contactperson_number: selectedItem?.ContactPersonNumber || '',
        credit_days: selectedItem?.CreditDays ?? 0,
        supplier_source: selectedItem?.SupplierSource || '',
      };
    } else {
      return {
        code: selectedItem?.Code || '',
        name: selectedItem?.Name || '',
        shortname: selectedItem?.ShortName || '',
        address: selectedItem?.Address || '',
        place: selectedItem?.Place || '',
        contactperson: selectedItem?.ContactPerson || '',
        number: selectedItem?.Number || '',
      };
    }
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema: getValidationSchema(),

    onSubmit: async (values) => {
      try {
        setLoading(true);
        const headers = {
          'Content-Type': 'application/json',
          'XApiKey': process.env.REACT_APP_API_KEY,
        };

        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        if (isSupplier) {
          console.log('hereee insideee 1', selectedItem);
          const payload = {
            supplierCode: values.supplier_code,
            supplierName: values.supplier_name,
            address: values.address,
            place: values.place,
            city: values.city,
            state: values.state,
            pincode: values.pincode,
            mobileNo: values.mobile_no,
            phone1: values.phone1,
            phone2: values.phone2 || '',
            email: values.email,
            gstNo: values.gst_num,
            contactPersonName: values.contactperson_name,
            contactPersonNumber: values.contactperson_number,
            creditDays: Number(values.credit_days || 0),
            supplierSource: values.supplier_source,
          };

          const endpoint = selectedItem ? PUT_SUPPLIER_MASTER : POST_SUPPLIER_MASTER;
          const requestHeaders = selectedItem
            ? { ...headers, supplierID: selectedItem?.SupplierID ?? 0 }
            : headers;

          const response = await axios.post(`${BASE_URL}${endpoint}`, payload, { headers: requestHeaders });

          if (response.status === 200) {
            toast.success(`Supplier ${selectedItem ? 'updated' : 'created'} successfully.`, { autoClose: 1200 });
            await onSave();
            handleCancel();
          }
        } else {
          console.log('hereee insideee 2', selectedItem);
          const payload = {
            id: selectedItem?.ManufactureID ?? selectedItem?.ManufactureID ?? 0,
            code: values.code,
            name: values.name,
            shortName: values.shortname,
            address: values.address,
            place: values.place,
            contactPerson: values.contactperson,
            number: values.number,
            isActive: selectedItem?.IsActive ?? true,
          };

          const endpoint = selectedItem ? PUT_MANUFACTURER_MASTER : POST_MANUFACTURER_MASTER;
          const response = await axios.post(`${BASE_URL}${endpoint}`, payload, { headers });

          if (response.status === 200) {
            toast.success(`Company ${selectedItem ? 'updated' : 'created'} successfully.`, { autoClose: 1200 });
            await onSave();
            handleCancel();
          }
        }
      } catch (error) {
        console.error(error);
        toast.error('Something went wrong while saving. Please try again.', { autoClose: 1500 });
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      {loading && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Processing...</p>
          </div>
        </div>
      )}
      
      <div className={`bg-white h-fit ${isSupplier ? 'w-4/5 max-w-5xl' : 'w-4/5 max-w-4xl'} rounded-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-zoom-in overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white flex items-center">
            {isSupplier ? (
              <FiFileText className="mr-2" />
            ) : (
              <FaBuilding className="mr-2" />
            )}
            {selectedItem ? `Edit ${mode}` : `Add New ${mode}`}
          </h2>
          <button 
            onClick={handleCancel}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors duration-200"
            aria-label="Close modal"
          >
            <FiX size={20} />
          </button>
        </div>
        
        <form onSubmit={formik.handleSubmit} className="p-8 h-[85vh] overflow-y-auto hide-scrollbar">
          {isSupplier ? (
            // Supplier Form Fields
            <>
              {/* Row 1: Supplier Code and Supplier Name */}
              <div className="grid grid-cols-2 gap-6">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiTag className="mr-2" /> Supplier Code <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiTag />
                    </div>
                    <input
                      type="text"
                      name="supplier_code"
                      placeholder="Enter Supplier Code"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.supplier_code && formik.errors.supplier_code ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.supplier_code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={codeRef}
                      onKeyDown={(e) => handleKeyDown(e, nameRef)}
                    />
                  </div>
                  {formik.touched.supplier_code && formik.errors.supplier_code && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.supplier_code}</p>
                  )}
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiFileText className="mr-2" /> Supplier Name <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiFileText />
                    </div>
                    <input
                      type="text"
                      name="supplier_name"
                      placeholder="Enter Supplier Name"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.supplier_name && formik.errors.supplier_name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.supplier_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={nameRef}
                      onKeyDown={(e) => handleKeyDown(e, addressRef)}
                    />
                  </div>
                  {formik.touched.supplier_name && formik.errors.supplier_name && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.supplier_name}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Address */}
              <div className="grid grid-cols-1 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiMapPin className="mr-2" /> Address
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiMapPin />
                    </div>
                    <input
                      type="text"
                      name="address"
                      placeholder="Enter Address"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.address && formik.errors.address ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={addressRef}
                      onKeyDown={(e) => handleKeyDown(e, placeRef)}
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Place, City, State */}
              <div className="grid grid-cols-3 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiMap className="mr-2" /> Place
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiMap />
                    </div>
                    <input
                      type="text"
                      name="place"
                      placeholder="Enter Place"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.place}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={placeRef}
                      onKeyDown={(e) => handleKeyDown(e, cityRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiNavigation className="mr-2" /> City
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiNavigation />
                    </div>
                    <input
                      type="text"
                      name="city"
                      placeholder="Enter City"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={cityRef}
                      onKeyDown={(e) => handleKeyDown(e, stateRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiGlobe className="mr-2" /> State
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiGlobe />
                    </div>
                    <input
                      type="text"
                      name="state"
                      placeholder="Enter State"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={stateRef}
                      onKeyDown={(e) => handleKeyDown(e, pincodeRef)}
                    />
                  </div>
                </div>
              </div>

              {/* Row 4: Pincode, Mobile, Phone1 */}
              <div className="grid grid-cols-3 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiHash className="mr-2" /> Pincode
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiHash />
                    </div>
                    <input
                      type="text"
                      name="pincode"
                      placeholder="Enter Pincode"
                      maxLength="6"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.pincode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={pincodeRef}
                      onKeyDown={(e) => handleKeyDown(e, mobileRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiPhone className="mr-2" /> Mobile Number
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiPhone />
                    </div>
                    <input
                      type="text"
                      name="mobile_no"
                      placeholder="Enter Mobile Number"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.mobile_no}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={mobileRef}
                      onKeyDown={(e) => handleKeyDown(e, phone1Ref)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiPhone className="mr-2" /> Phone1
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiPhone />
                    </div>
                    <input
                      type="text"
                      name="phone1"
                      placeholder="Enter Phone1"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.phone1}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={phone1Ref}
                      onKeyDown={(e) => handleKeyDown(e, phone2Ref)}
                    />
                  </div>
                </div>
              </div>

              {/* Row 5: Phone2, Email and GST Number */}
              <div className="grid grid-cols-3 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiPhone className="mr-2" /> Phone2
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiPhone />
                    </div>
                    <input
                      type="text"
                      name="phone2"
                      placeholder="Enter Phone2 (Optional)"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.phone2}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={phone2Ref}
                      onKeyDown={(e) => handleKeyDown(e, emailRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiMail className="mr-2" /> Email
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiMail />
                    </div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter Email"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={emailRef}
                      onKeyDown={(e) => handleKeyDown(e, gstNumRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiHash className="mr-2" /> GST Number
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiHash />
                    </div>
                    <input
                      type="text"
                      name="gst_num"
                      placeholder="Enter GST Number"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.gst_num}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={gstNumRef}
                      onKeyDown={(e) => handleKeyDown(e, contactPersonNameRef)}
                    />
                  </div>
                </div>
              </div>

              {/* Row 6: Contact Person Name, Contact Person Number, Credit Days */}
              <div className="grid grid-cols-3 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiUser className="mr-2" /> Contact Person Name
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiUser />
                    </div>
                    <input
                      type="text"
                      name="contactperson_name"
                      placeholder="Enter Contact Person Name"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.contactperson_name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={contactPersonNameRef}
                      onKeyDown={(e) => handleKeyDown(e, contactPersonNumberRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiPhone className="mr-2" /> Contact Person Number
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiPhone />
                    </div>
                    <input
                      type="text"
                      name="contactperson_number"
                      placeholder="Enter Contact Person Number"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.contactperson_number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={contactPersonNumberRef}
                      onKeyDown={(e) => handleKeyDown(e, creditDaysRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiHash className="mr-2" /> Credit Days
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiHash />
                    </div>
                    <input
                      type="number"
                      name="credit_days"
                      placeholder="Enter Credit Days"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.credit_days}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={creditDaysRef}
                      onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">Supplier Category</label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiTag />
                    </div>
                    <select
                      name="supplier_source"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.supplier_source}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={supplierSourceRef}
                      onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                    >
                      <option value="">Select Supplier Category</option>
                      <option value="Lab">Lab</option>
                      <option value="Pharmacy">Pharmacy</option>
                    </select>
                  </div>
                  {formik.touched.supplier_source && formik.errors.supplier_source && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.supplier_source}</p>
                  )}
                </div>






                
              </div>
            </>
          ) : (
            // Company Form Fields
            <>
              {/* Row 1: Code and Name */}
              <div className="grid grid-cols-2 gap-6">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiTag className="mr-2" /> Code <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiTag />
                    </div>
                    <input
                      type="text"
                      name="code"
                      placeholder="Enter Code"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.code && formik.errors.code ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.code}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={codeRef}
                      onKeyDown={(e) => handleKeyDown(e, nameRef)}
                    />
                  </div>
                  {formik.touched.code && formik.errors.code && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.code}</p>
                  )}
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiFileText className="mr-2" /> Name <span className="text-red-500 ml-1">*</span>
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FaBuilding />
                    </div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter Company Name"
                      className={`w-full pl-10 pr-3 py-3 border ${
                        formik.touched.name && formik.errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                      } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={nameRef}
                      onKeyDown={(e) => handleKeyDown(e, shortNameRef)}
                    />
                  </div>
                  {formik.touched.name && formik.errors.name && (
                    <p className="mt-1 text-sm text-red-500">{formik.errors.name}</p>
                  )}
                </div>
              </div>

              {/* Row 2: Short Name and Address */}
              <div className="grid grid-cols-2 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiTag className="mr-2" /> Short Name
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiTag />
                    </div>
                    <input
                      type="text"
                      name="shortname"
                      placeholder="Enter Short Name"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.shortname}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={shortNameRef}
                      onKeyDown={(e) => handleKeyDown(e, addressRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiMapPin className="mr-2" /> Address
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiMapPin />
                    </div>
                    <input
                      type="text"
                      name="address"
                      placeholder="Enter Address"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={addressRef}
                      onKeyDown={(e) => handleKeyDown(e, placeRef)}
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Place, Contact Person, Number */}
              <div className="grid grid-cols-3 gap-6 mt-5">
                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiMap className="mr-2" /> Place
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiMap />
                    </div>
                    <input
                      type="text"
                      name="place"
                      placeholder="Enter Place"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.place}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={placeRef}
                      onKeyDown={(e) => handleKeyDown(e, contactPersonNameRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiUser className="mr-2" /> Contact Person
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiUser />
                    </div>
                    <input
                      type="text"
                      name="contactperson"
                      placeholder="Enter Contact Person"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.contactperson}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={contactPersonNameRef}
                      onKeyDown={(e) => handleKeyDown(e, contactPersonNumberRef)}
                    />
                  </div>
                </div>

                <div className="w-full">
                  <div className="flex justify-between mb-1.5">
                    <label className="text-sm font-medium text-black-700 flex items-center">
                      <FiPhone className="mr-2" /> Number
                    </label>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-indigo-400">
                      <FiPhone />
                    </div>
                    <input
                      type="text"
                      name="number"
                      placeholder="Enter Number"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors duration-200`}
                      value={formik.values.number}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      ref={contactPersonNumberRef}
                      onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-6 mt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-medium text-black-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors duration-200 flex items-center"
              onClick={handleCancel}
            >
              <FiX className="mr-2" /> Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
              ref={saveButtonRef}
            >
              <FiCheck className="mr-2" /> {selectedItem ? `Update ${mode}` : `Save ${mode}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSupplierModal;
